/**
 * Performance Monitoring Utilities
 * Tracks application performance metrics and user experience
 */

// Browser APIs missing from the TS DOM lib (Chromium-only / non-standard).
interface NavigatorWithConnection extends Navigator {
  connection?: { effectiveType?: string }
}

interface PerformanceWithMemory extends Performance {
  memory?: { usedJSHeapSize: number }
}

interface LayoutShiftEntry extends PerformanceEntry {
  hadRecentInput: boolean
  value: number
}

export interface PerformanceMetrics {
  pageLoadTime: number
  timeToInteractive: number
  firstContentfulPaint: number
  largestContentfulPaint: number
  cumulativeLayoutShift: number
  firstInputDelay: number
  memoryUsage: number
  connectionType: string
  deviceType: 'mobile' | 'tablet' | 'desktop'
  screenResolution: string
}

export class PerformanceMonitor {
  private metrics: Partial<PerformanceMetrics> = {}

  constructor() {
    this.init()
  }

  private init() {
    // Check if we're in a browser environment
    if (typeof window === 'undefined') return

    // Start measuring immediately
    performance.now()

    // Track page load metrics
    this.trackPageLoadMetrics()

    // Track device and connection info
    this.trackDeviceInfo()

    // Set up performance observers
    this.setupPerformanceObservers()
  }

  private trackPageLoadMetrics() {
    if (!window.performance) return

    // Page load time
    if (window.performance.timing) {
      const navigation = window.performance.timing
      this.metrics.pageLoadTime = navigation.loadEventEnd - navigation.navigationStart
      this.metrics.timeToInteractive = navigation.domInteractive - navigation.navigationStart
    }

    // Navigation timing API
    const observer = new PerformanceObserver((list) => {
      const entries = list.getEntries()
      entries.forEach((entry) => {
        if (entry.entryType === 'navigation') {
          const navEntry = entry as PerformanceNavigationTiming
          this.metrics.pageLoadTime = navEntry.loadEventEnd - navEntry.fetchStart
          this.metrics.timeToInteractive = navEntry.domInteractive - navEntry.fetchStart
        }
      })
    })
    observer.observe({ entryTypes: ['navigation'] })
  }

  private trackDeviceInfo() {
    if (!window.navigator) return

    // Connection info
    if ('connection' in navigator) {
      const connection = (navigator as NavigatorWithConnection).connection
      this.metrics.connectionType = connection?.effectiveType || 'unknown'
    } else {
      this.metrics.connectionType = 'unknown'
    }

    // Device type
    const width = window.innerWidth
    if (width < 768) {
      this.metrics.deviceType = 'mobile'
    } else if (width < 1024) {
      this.metrics.deviceType = 'tablet'
    } else {
      this.metrics.deviceType = 'desktop'
    }

    // Screen resolution
    this.metrics.screenResolution = `${window.screen.width}x${window.screen.height}`

    // Memory usage (if available)
    const memory = (performance as PerformanceWithMemory).memory
    if (memory) {
      this.metrics.memoryUsage = memory.usedJSHeapSize / 1024 / 1024 // MB
    }
  }

  private setupPerformanceObservers() {
    if (!window.PerformanceObserver) return

    // Largest Contentful Paint
    try {
      const lcpObserver = new PerformanceObserver((list) => {
        const entries = list.getEntries()
        const lastEntry = entries[entries.length - 1]
        this.metrics.largestContentfulPaint = lastEntry.startTime
      })
      lcpObserver.observe({ entryTypes: ['largest-contentful-paint'] })
    } catch (e) {
      console.warn('LCP observer not supported:', e)
    }

    // First Input Delay
    try {
      const fidObserver = new PerformanceObserver((list) => {
        const entries = list.getEntries()
        entries.forEach((entry) => {
          this.metrics.firstInputDelay = (entry as PerformanceEventTiming).processingStart - entry.startTime
        })
      })
      fidObserver.observe({ entryTypes: ['first-input'] })
    } catch (e) {
      console.warn('FID observer not supported:', e)
    }

    // Cumulative Layout Shift
    try {
      let clsValue = 0
      const clsObserver = new PerformanceObserver((list) => {
        const entries = list.getEntries()
        entries.forEach((entry) => {
          const shift = entry as LayoutShiftEntry
          if (!shift.hadRecentInput) {
            clsValue += shift.value
          }
        })
        this.metrics.cumulativeLayoutShift = clsValue
      })
      clsObserver.observe({ entryTypes: ['layout-shift'] })
    } catch (e) {
      console.warn('CLS observer not supported:', e)
    }
  }

  public getMetrics(): PerformanceMetrics {
    return {
      pageLoadTime: this.metrics.pageLoadTime || 0,
      timeToInteractive: this.metrics.timeToInteractive || 0,
      firstContentfulPaint: this.metrics.firstContentfulPaint || 0,
      largestContentfulPaint: this.metrics.largestContentfulPaint || 0,
      cumulativeLayoutShift: this.metrics.cumulativeLayoutShift || 0,
      firstInputDelay: this.metrics.firstInputDelay || 0,
      memoryUsage: this.metrics.memoryUsage || 0,
      connectionType: this.metrics.connectionType || 'unknown',
      deviceType: this.metrics.deviceType || 'desktop',
      screenResolution: this.metrics.screenResolution || 'unknown'
    }
  }

  public logMetrics() {
    const metrics = this.getMetrics()
    console.log('🚀 Performance Metrics:', metrics)

    // Log warnings for poor performance
    if (metrics.pageLoadTime > 3000) {
      console.warn('⚠️ Slow page load detected:', metrics.pageLoadTime, 'ms')
    }
    if (metrics.largestContentfulPaint > 2500) {
      console.warn('⚠️ Slow LCP detected:', metrics.largestContentfulPaint, 'ms')
    }
    if (metrics.cumulativeLayoutShift > 0.25) {
      console.warn('⚠️ High CLS detected:', metrics.cumulativeLayoutShift)
    }
    if (metrics.firstInputDelay > 100) {
      console.warn('⚠️ High FID detected:', metrics.firstInputDelay, 'ms')
    }
  }

  public getPerformanceScore(): number {
    const metrics = this.getMetrics()
    let score = 100

    // Page load time (ideal: <2000ms, good: <3000ms)
    if (metrics.pageLoadTime > 2000) score -= Math.min(20, (metrics.pageLoadTime - 2000) / 100)

    // LCP (ideal: <1200ms, good: <2500ms)
    if (metrics.largestContentfulPaint > 1200) score -= Math.min(20, (metrics.largestContentfulPaint - 1200) / 65)

    // CLS (ideal: <0.1, good: <0.25)
    if (metrics.cumulativeLayoutShift > 0.1) score -= Math.min(20, (metrics.cumulativeLayoutShift - 0.1) * 133)

    // FID (ideal: <100ms, good: <300ms)
    if (metrics.firstInputDelay > 100) score -= Math.min(20, (metrics.firstInputDelay - 100) / 10)

    // Memory usage (penalty for >50MB)
    if (metrics.memoryUsage > 50) score -= Math.min(20, (metrics.memoryUsage - 50) / 5)

    return Math.max(0, Math.round(score))
  }

  public getPerformanceGrade(): 'A' | 'B' | 'C' | 'D' | 'F' {
    const score = this.getPerformanceScore()
    if (score >= 90) return 'A'
    if (score >= 80) return 'B'
    if (score >= 70) return 'C'
    if (score >= 60) return 'D'
    return 'F'
  }
}

// Singleton instance
export const performanceMonitor = new PerformanceMonitor()

// Export convenience functions
export const getPerformanceMetrics = () => performanceMonitor.getMetrics()
export const getPerformanceScore = () => performanceMonitor.getPerformanceScore()
export const getPerformanceGrade = () => performanceMonitor.getPerformanceGrade()
export const logPerformanceMetrics = () => performanceMonitor.logMetrics()